import { useState, useRef, forwardRef, useImperativeHandle } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Upload, X, ArrowRight, Loader2, Image as ImageIcon } from 'lucide-react';
import OrbisSpinner from '@/components/ui/orbis-spinner';
import QualityPicker from '@/components/ui/quality-picker';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { useCredits } from '@/contexts/CreditsContext';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Model selection is now handled by QualityPicker (quality tiers instead of raw model IDs).
// Backend call_with_fallback() picks the optimal model from STAGE_MODEL_POOLS.
// user_preferred override only sent when user explicitly selects Quality/Premium tier.

const S = {
  label: { fontSize: '11px', fontWeight: 400, letterSpacing: '0.06em', textTransform: 'uppercase', color: '#5A5855' },
  fieldLabel: { fontSize: '12px', fontWeight: 400, color: '#8A8882' },
  input: { background: '#2A2A2A', border: '0.5px solid rgba(255,255,255,0.08)', borderRadius: '6px', height: '44px', padding: '0 14px', fontSize: '14px', fontWeight: 300, color: 'var(--orbis-warm)', fontFamily: 'var(--orbis-font)', outline: 'none', width: '100%' },
  textarea: { background: '#2A2A2A', border: '0.5px solid rgba(255,255,255,0.08)', borderRadius: '6px', padding: '12px 14px', fontSize: '14px', fontWeight: 300, color: 'var(--orbis-warm)', fontFamily: 'var(--orbis-font)', outline: 'none', width: '100%', resize: 'none' },
};

const CloneBriefStage = forwardRef(function CloneBriefStage({ projectId, analysis, initialData, isProcessing, onComplete }, ref) {
  const { tier } = useCredits();

  const [companyName, setCompanyName] = useState(initialData?.company_name || '');
  const [industry, setIndustry] = useState(initialData?.industry || '');
  const [description, setDescription] = useState(initialData?.description || '');
  const [model, setModel] = useState(initialData?.model || '');
  const [premiumMode, setPremiumMode] = useState(initialData?.premium_mode || false);
  const [conversionGoal, setConversionGoal] = useState(initialData?.conversion_goal || '');
  const [conversionGoalCustom, setConversionGoalCustom] = useState(initialData?.conversion_goal_custom || '');
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [isUploadingLogo, setIsUploadingLogo] = useState(false);
  const logoInputRef = useRef(null);

  // Expose setFields/getFields so the agent panel can auto-fill from natural language
  useImperativeHandle(ref, () => ({
    setFields: (fields) => {
      if (fields.company_name && !companyName) setCompanyName(fields.company_name);
      if (fields.industry && !industry) setIndustry(fields.industry);
      if (fields.description && !description) setDescription(fields.description);
    },
    getFields: () => ({
      company_name: companyName,
      industry,
      description,
      model,
      premium_mode: premiumMode,
      conversion_goal: conversionGoal,
      conversion_goal_custom: conversionGoalCustom,
    }),
  }));

  const handleLogoSelect = async (file) => {
    if (!file) return;
    setLogoFile(file);
    setLogoPreview(URL.createObjectURL(file));

    // Upload immediately
    setIsUploadingLogo(true);
    try {
      const form = new FormData();
      form.append('file', file);
      await axios.post(`${API_URL}/magic/projects/${projectId}/logo`, form, {
        headers: {
          'Content-Type': 'multipart/form-data',
          Authorization: `Bearer ${localStorage.getItem('orbis_token')}`,
        },
      });
    } catch {
      toast.error('Logo upload failed');
    } finally {
      setIsUploadingLogo(false);
    }
  };

  const handleSubmit = () => {
    if (!companyName.trim()) { toast.error('Company name is required'); return; }
    onComplete({ company_name: companyName, industry, description, model, premium_mode: premiumMode, conversion_goal: conversionGoal, conversion_goal_custom: conversionGoalCustom });
  };

  // Context hint from analysis
  const vibeHint = analysis?.vibe;
  const styleHint = analysis?.style_keywords?.slice(0, 3).join(', ');

  return (
    <div className="flex-1 flex min-h-0">
      {/* Form panel */}
      <div
        className="flex flex-col gap-5 flex-shrink-0 overflow-auto"
        style={{ width: '400px', background: '#1A1A1A', borderRight: '0.5px solid rgba(255,255,255,0.08)', padding: '28px 24px' }}
      >
        <div>
          <h2 style={{ fontSize: '18px', fontWeight: 200, letterSpacing: '-0.03em', color: 'var(--orbis-warm)', marginBottom: '6px' }}>Your Brand</h2>
          <p style={{ fontSize: '13px', fontWeight: 300, color: '#5A5855' }}>Tell us about your company so the clone reflects your identity.</p>
        </div>

        {/* Analysis context pill */}
        {(vibeHint || styleHint) && (
          <div style={{ background: 'rgba(184,204,223,0.06)', border: '0.5px solid rgba(184,204,223,0.15)', borderRadius: '8px', padding: '10px 14px' }}>
            <p style={{ fontSize: '12px', fontWeight: 300, color: 'var(--orbis-blue)' }}>
              Reference detected: {[vibeHint, styleHint].filter(Boolean).join(' · ')}
            </p>
          </div>
        )}

        {/* Company Name */}
        <div className="flex flex-col gap-1.5">
          <label style={S.fieldLabel}>Company Name <span style={{ color: 'var(--orbis-blue)' }}>*</span></label>
          <input value={companyName} onChange={e => setCompanyName(e.target.value)} placeholder="Acme Inc" style={S.input} />
        </div>

        {/* Industry */}
        <div className="flex flex-col gap-1.5">
          <label style={S.fieldLabel}>Industry</label>
          <input value={industry} onChange={e => setIndustry(e.target.value)} placeholder="SaaS · Fintech · E-commerce" style={S.input} />
        </div>

        {/* Description */}
        <div className="flex flex-col gap-1.5">
          <label style={S.fieldLabel}>Description</label>
          <textarea
            value={description}
            onChange={e => setDescription(e.target.value)}
            placeholder="A brief description of your product or service..."
            rows={3}
            style={S.textarea}
          />
        </div>

        {/* Conversion Goal */}
        <div className="flex flex-col gap-1.5">
          <label style={S.fieldLabel}>Primary Conversion Goal <span style={{ color: 'var(--orbis-blue)' }}>*</span></label>
          <Select value={conversionGoal} onValueChange={v => { setConversionGoal(v); if (v !== 'custom') setConversionGoalCustom(''); }}>
            <SelectTrigger style={{ background: '#2A2A2A', border: '0.5px solid rgba(255,255,255,0.08)', borderRadius: '6px', height: '44px', color: conversionGoal ? 'var(--orbis-warm)' : '#5A5855', fontSize: '14px' }}>
              <SelectValue placeholder="What should visitors do?" />
            </SelectTrigger>
            <SelectContent style={{ background: '#2A2A2A', border: '0.5px solid rgba(255,255,255,0.08)' }}>
              {[
                { value: 'book_demo', label: 'Book a demo' },
                { value: 'start_trial', label: 'Start free trial' },
                { value: 'get_quote', label: 'Get a quote' },
                { value: 'buy_now', label: 'Buy now' },
                { value: 'contact_sales', label: 'Contact sales' },
                { value: 'custom', label: 'Custom...' },
              ].map(g => (
                <SelectItem key={g.value} value={g.value} style={{ color: '#8A8882', fontSize: '14px' }}>{g.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          {conversionGoal === 'custom' && (
            <input
              value={conversionGoalCustom}
              onChange={e => setConversionGoalCustom(e.target.value)}
              placeholder="e.g., Download whitepaper"
              style={S.input}
            />
          )}
        </div>

        {/* Logo upload */}
        <div className="flex flex-col gap-2">
          <label style={S.fieldLabel}>Logo <span style={{ fontSize: '11px', color: '#5A5855' }}>(optional)</span></label>
          {logoPreview ? (
            <div className="flex items-center gap-3">
              <div style={{ width: '56px', height: '56px', borderRadius: '8px', background: '#2A2A2A', border: '0.5px solid rgba(255,255,255,0.08)', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}>
                <img src={logoPreview} alt="Logo" className="w-full h-full object-contain" style={{ padding: '6px' }} />
              </div>
              <div>
                <p style={{ fontSize: '13px', fontWeight: 300, color: 'var(--orbis-warm)' }}>{logoFile?.name}</p>
                {isUploadingLogo
                  ? <p style={{ fontSize: '12px', color: 'var(--orbis-blue)' }}>Uploading...</p>
                  : <p style={{ fontSize: '12px', color: 'var(--orbis-green)' }}>Uploaded ✓</p>
                }
              </div>
              <button onClick={() => { setLogoFile(null); setLogoPreview(null); }} className="ml-auto" style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#5A5855' }}>
                <X className="w-4 h-4" />
              </button>
            </div>
          ) : (
            <button
              onClick={() => logoInputRef.current?.click()}
              className="flex items-center gap-3 w-full transition-colors"
              style={{
                background: '#111111', border: '0.5px dashed rgba(255,255,255,0.12)', borderRadius: '8px',
                padding: '14px 16px', cursor: 'pointer',
              }}
            >
              <ImageIcon className="w-5 h-5 flex-shrink-0" style={{ color: '#5A5855' }} />
              <div className="text-left">
                <p style={{ fontSize: '13px', fontWeight: 300, color: '#8A8882' }}>Upload your logo</p>
                <p style={{ fontSize: '12px', fontWeight: 300, color: '#5A5855' }}>PNG, SVG, JPG — will be applied to the site</p>
              </div>
              <Upload className="w-4 h-4 ml-auto flex-shrink-0" style={{ color: '#5A5855' }} />
            </button>
          )}
          <input ref={logoInputRef} type="file" accept="image/*" className="hidden" onChange={e => e.target.files?.[0] && handleLogoSelect(e.target.files[0])} />
        </div>

        {/* Divider */}
        <div style={{ height: '0.5px', background: 'rgba(255,255,255,0.07)' }} />

        {/* Generation Quality — replaces raw model picker + premium toggle */}
        <QualityPicker tier={tier} value={model} onChange={setModel} />

        {/* Spacer */}
        <div className="flex-1" />

        {/* Generate button */}
        <button
          onClick={handleSubmit}
          disabled={isProcessing || !companyName.trim()}
          className="flex items-center justify-center gap-2 w-full transition-opacity"
          style={{
            height: '48px', borderRadius: '100px', background: 'var(--orbis-blue)', border: 'none',
            fontSize: '14px', fontWeight: 400, color: '#1A1A1A',
            cursor: (isProcessing || !companyName.trim()) ? 'not-allowed' : 'pointer',
            opacity: (isProcessing || !companyName.trim()) ? 0.5 : 1,
          }}
        >
          {isProcessing ? <OrbisSpinner size={16} color="#1A1A1A" /> : <ArrowRight className="w-4 h-4" />}
          {isProcessing ? 'Starting build...' : 'Generate Clone'}
        </button>
      </div>

      {/* Right info panel */}
      <div className="flex-1 flex flex-col items-center justify-center p-12 gap-8" style={{ background: '#111111' }}>
        <div className="max-w-xs text-center">
          <h3 style={{ fontSize: '20px', fontWeight: 200, letterSpacing: '-0.04em', color: 'var(--orbis-warm)', marginBottom: '12px' }}>
            We'll build your<br />version of the site.
          </h3>
          <p style={{ fontSize: '14px', fontWeight: 300, color: '#8A8882', lineHeight: 1.75 }}>
            Your company information will be used to personalise the cloned design with your own brand identity and content.
          </p>
        </div>

        {/* Reference analysis summary */}
        {analysis && (
          <div style={{ width: '100%', maxWidth: '320px', background: '#161616', border: '0.5px solid rgba(255,255,255,0.07)', borderRadius: '12px', padding: '16px 20px' }}>
            <p style={{ fontSize: '10px', letterSpacing: '0.1em', textTransform: 'uppercase', color: '#5A5855', marginBottom: '12px' }}>Reference detected</p>
            <div className="flex flex-col gap-3">
              {Object.keys(analysis.colors || {}).length > 0 && (
                <div className="flex items-center gap-2">
                  <span style={{ fontSize: '12px', color: '#5A5855', width: '60px' }}>Colors</span>
                  <div className="flex gap-1.5">
                    {Object.values(analysis.colors).map((c, i) => (
                      <div key={i} style={{ width: '16px', height: '16px', borderRadius: '50%', background: c, border: '0.5px solid rgba(255,255,255,0.08)' }} />
                    ))}
                  </div>
                </div>
              )}
              {analysis.fonts?.length > 0 && (
                <div className="flex items-center gap-2">
                  <span style={{ fontSize: '12px', color: '#5A5855', width: '60px' }}>Fonts</span>
                  <span style={{ fontSize: '12px', color: '#8A8882' }}>{analysis.fonts.slice(0, 2).join(', ')}</span>
                </div>
              )}
              {analysis.vibe && (
                <div className="flex items-center gap-2">
                  <span style={{ fontSize: '12px', color: '#5A5855', width: '60px' }}>Vibe</span>
                  <span style={{ fontSize: '12px', color: '#8A8882', fontStyle: 'italic' }}>{analysis.vibe}</span>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
});

export default CloneBriefStage;
